import { Component } from "react";
import { useLocation } from "react-router-dom";
import { ErrorPage } from "../ErrorPage/ErrorPage";

/**
 * A wrapper for the ErrorBoundary component that resets the error state when the route changes.
 *
 * This ensures that navigation to a new route clears any previous errors caught by the boundary.
 *
 * @component
 * @param {Object} props - React component props.
 * @param {React.ReactNode} props.children - The child components to be wrapped inside the error boundary.
 * @returns {JSX.Element} Wrapped component with error boundary functionality.
 */
export const ErrorBoundary = ({ children }) => {
  const location = useLocation();

  return (
    <ErrorBoundaryHandler location={location}>{children}</ErrorBoundaryHandler>
  );
};

class ErrorBoundaryHandler extends Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError() {
    return { hasError: true };
  }

  componentDidUpdate(prevProps) {
    const { location } = this.props;

    if (prevProps.location !== location) {
      this.setState({ hasError: false });
    }
  }

  componentDidCatch(error, errorInfo) {
    // eslint-disable-next-line no-console
    console.error("Error caught by ErrorBoundary:", error, errorInfo);
  }

  render() {
    const { hasError } = this.state;
    const { children } = this.props;

    if (hasError) {
      return <ErrorPage />;
    }

    return children;
  }
}
